import React, { useRef, useState, useEffect, useCallback } from "react";
import useOutsideClick from "../hooks/useOutsideClick";
import { api } from "../actions/api";
import { Link, useNavigate } from "react-router-dom";
import formatMessageTime from "../Utils/formattedTime";
// import { formatMessageTime } from "../Utils/formattedTime";

const HeaderMessage = ({ isDropdownVisible, toggleDropdown }) => {
  const [messages, setMessages] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const dropdownListRef = useRef(null); // Ref for the scrollable list
  const navigate = useNavigate();

  // Foreground push updates are not handled here to avoid duplicate listeners

  const fetchNotifications = useCallback(
    async (requestedPage) => {
      // Prefer explicitly requested page, otherwise use current state
      const pageToFetch =
        typeof requestedPage === "number" ? requestedPage : page;
      if (loading || !hasMore) return;

      setLoading(true);
      try {
        const res = await api.get("/notifications", {
          params: { page: pageToFetch },
        });

        const newNotifications = res.data.data.notifications || [];

        setMessages((prevMessages) => [...prevMessages, ...newNotifications]);
        setHasMore(res.data.meta.last_page > pageToFetch);
        // Advance page relative to what we actually fetched
        setPage(pageToFetch + 1);
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
      } finally {
        setLoading(false);
      }
    },
    [loading, hasMore, page],
  );

  useEffect(() => {
    if (isDropdownVisible) {
      // Reset state and fetch first page when dropdown is opened
      setMessages([]);
      setHasMore(true);
      // Fetch starting from page 1 explicitly to avoid stale closures
      fetchNotifications(1);
    }
  }, [isDropdownVisible]);

  useEffect(() => {
    const handleScroll = () => {
      const dropdownElement = dropdownListRef.current;
      if (
        dropdownElement &&
        dropdownElement.scrollHeight - dropdownElement.scrollTop <=
          dropdownElement.clientHeight + 100 // Add a buffer for triggering
      ) {
        fetchNotifications();
      }
    };

    const dropdownListElement = dropdownListRef.current;
    if (dropdownListElement) {
      dropdownListElement.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (dropdownListElement) {
        dropdownListElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, [fetchNotifications]);

  useOutsideClick(dropdownRef, () => {
    if (isDropdownVisible) {
      setMessages([]);
      toggleDropdown();
    }
  });

  // No onMessage listener here; index.js shows toast and this component fetches from API

  const handleTribeNotificationClick = (notificationData, tribeUrl) => {
    const groupId = notificationData?.groupId;
    const postId = notificationData?.postId;
    // const commentId = notificationData?.commentId;

    // const openCommentBox = notificationData?.comment_like && notificationData?.commentId;

    if (groupId && postId) {
      // Close the dropdown
      toggleDropdown();

      navigate("/tribe", {
        state: {
          selectedGroup: groupId,
          selectedPost: postId,
          // selectedCommentId: commentId,
          scrollToPost: true,
          // openCommentBox: openCommentBox
        },
      });
      return;
    }

    // Fallback to URL-based navigation if groupId/postId not available
    if (
      typeof tribeUrl === "string" &&
      tribeUrl.includes("/user/tribe-group")
    ) {
      toggleDropdown();
      navigate("/tribe");
      return;
    }
  };

  const handleChapterVideoUploadedNotificationClick = (notificationData) => {
    navigate(`/modules/${notificationData?.module_id}/chapters`, {
      state: {
        chapterId: notificationData?.chapter_id,
      },
    });
  };

  const handleLevelUpNotificationClick = (notificationData) => {
    const newLevel = parseInt(notificationData?.level, 10);

    // Validate that we have a valid level number
    if (isNaN(newLevel) || newLevel < 1) {
      console.error(
        "Invalid level in level up notification:",
        notificationData?.level,
      );
      return;
    }

    const oldLevel = newLevel > 1 ? newLevel - 1 : 1;

    // Close the dropdown
    toggleDropdown();

    // Dispatch levelUp event to open the LevelUpModal
    const levelUpEvent = new CustomEvent("levelUp", {
      detail: {
        newLevel: newLevel,
        oldLevel: oldLevel,
      },
    });
    window.dispatchEvent(levelUpEvent);
    return;
  };

  const handleNotificationClick = (n, e) => {
    // Prevent event from bubbling up to toggle the dropdown
    if (e) {
      e.stopPropagation();
    }

    // Extract notification data
    const notificationData = n?.data?.data;
    const tribeUrl = n?.url || n?.data?.data?.url;

    // Handle chapter video uploaded notification click
    if (
      (notificationData?.action &&
        notificationData?.action === "chapter_video_uploaded") ||
      (notificationData?.chapter_id && notificationData?.module_id)
    ) {
      handleChapterVideoUploadedNotificationClick(notificationData);
    }

    // Check if this is a level up notification
    if (notificationData?.action === "show_level_up_modal") {
      handleLevelUpNotificationClick(notificationData);
      return;
    }

    handleTribeNotificationClick(notificationData, tribeUrl);
  };

  return (
    <div className="header-icons" ref={dropdownRef} onClick={toggleDropdown}>
      <img alt="bell icon" className="main-icons" src="/icons/bell.svg" />
      {/* <img
        alt="notification dot"
        className="notification-dot"
        src="/icons/Skeleton.svg"
        style={{
          display: notifications.length === 0 ? "none" : "block"
        }}
      /> */}

      {isDropdownVisible && (
        <div
          className="notification-dropdown"
          ref={dropdownListRef}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "rgba(28, 41, 52, 0.6)",
            backdropFilter: "blur(5px) saturate(10%)",
            WebkitBackdropFilter: "blur(5px) saturate(120%)",
            color: "#fff",
          }}
        >
          {messages?.length === 0 && !loading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "200px",
                color: "#D0D8DF",
              }}
            >
              No notification available !
            </div>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {messages.map((n, idx) => (
                <li
                  key={n.id}
                  onClick={(e) => handleNotificationClick(n, e)}
                  style={{ padding: "10px 12px", cursor: "pointer" }}
                >
                  <div
                    className="text-body-b3"
                    style={{ fontWeight: 600, color: "#fff" }}
                  >
                    {n?.data?.title || "Notification"}
                  </div>
                  <div
                    className="text-body-b4"
                    style={{ opacity: 0.9, color: "#fff" }}
                  >
                    {n?.data?.body}
                  </div>
                  <div
                    className="text-caption-c2"
                    style={{ opacity: 0.7, marginTop: "10px", color: "#fff" }}
                  >
                    {formatMessageTime(n?.created_at)}
                  </div>
                  {/* <div style={{ height: '1px', background: '#2A4052', marginTop: '10px' }} /> */}
                </li>
              ))}
              {loading && (
                <li style={{ padding: "10px 12px" }}>Loading more...</li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default HeaderMessage;
